import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useState } from 'react';
import { UseMantineTableDataOperationsPartialProps } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperationsFromUrlParamState';
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
} from '@dealstack/frontend/shared/components/Table/MantineTable/constants';
import { useIsFiltersApplied } from './hooks/useIsFiltersApplied';
import { useResetPageOnQueryChange } from './hooks/useResetPageOnQueryChange';

const defaultPagination: PaginationState = {
	pageIndex: DEFAULT_PAGE_INDEX,
	pageSize: DEFAULT_PAGE_SIZE,
};

export const useMantineTableDataOperations = (
	props: UseMantineTableDataOperationsPartialProps = {}
) => {
	const {
		initialSorting = [],
		initialPagination = defaultPagination,
		initialQuickFilters = {},
		queryScope,
	} = props;
	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
	const [globalSearchString, setGlobalSearchString] = useState<string>();
	const [showGlobalSearchStringInput, setShowGlobalSearchStringInput] = useState(false);
	const [sorting, setSorting] = useState<SortingState>(initialSorting);
	const [pagination, setPagination] = useState<PaginationState>(initialPagination);
	const [quickFilters, setQuickFilters] =
		useState<Record<string, boolean | string>>(initialQuickFilters);

	// queryScope is for filters this hook doesn't manage, such as a bulk-action mode.
	useResetPageOnQueryChange(
		[columnFilters, globalSearchString, quickFilters, queryScope],
		pagination,
		setPagination
	);

	const resetAll = useCallback(() => {
		setColumnFilters([]);
		setGlobalSearchString(undefined);
		setShowGlobalSearchStringInput(false);
		setSorting([]);
		setPagination(initialPagination);
		setQuickFilters({});
	}, [initialPagination]);

	const isFiltersApplied = useIsFiltersApplied(columnFilters, globalSearchString);

	return {
		columnFilters,
		setColumnFilters,
		globalSearchString,
		setGlobalSearchString,
		sorting,
		setSorting,
		pagination,
		setPagination,
		showGlobalSearchStringInput,
		setShowGlobalSearchStringInput,
		isFiltersApplied,
		resetAll,
		quickFilters,
		setQuickFilters,
	};
};
export type MantineTableDataOperations = ReturnType<typeof useMantineTableDataOperations>;
